import { Button } from "@dropbox/dig-components/buttons";
import { Menu } from "@dropbox/dig-components/menu";
import { Text } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import {
  CheckmarkLine,
  ChevronDownLine,
  ChevronLeftLine,
  ChevronRightLine,
  ZoomInLine,
  ZoomOutLine,
} from "@dropbox/dig-icons/assets";
import type { FileDownloadStatus } from "@mirage/analytics/events/enums/file_download_status";
import { FileViewer } from "@mirage/mosaics/FileViewer/FileViewer";
import { tagged } from "@mirage/service-logging";
import { isPdfFile } from "@mirage/shared/utils/file-type";
import classNames from "classnames";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ZOOM_LEVELS } from "./constants";
import styles from "./DocumentPreviewArea.module.css";
import { EnhancedFileViewer } from "./EnhancedFileViewer";
import { useFileViewerPagination } from "./useFileViewerPagination";

import type { SearchResult } from "@mirage/service-dbx-api";

const logger = tagged("document-preview-area");

export const getInitialPageCount = (result: SearchResult): number => {
  if (isPdfFile(result)) {
    type PageCountMeta = {
      pageCount?: number;
      numPages?: number;
      media?: { pageCount?: number };
    };

    const meta = result as unknown as PageCountMeta;

    const pdfPageCount =
      meta.pageCount ?? meta.numPages ?? meta.media?.pageCount;

    if (typeof pdfPageCount === "number" && pdfPageCount > 0) {
      return pdfPageCount;
    }

    return 1;
  }

  if (
    result.mimeType?.startsWith("image/") ||
    result.mimeType?.startsWith("text/")
  ) {
    return 1;
  }

  return 1;
};

type DocumentPreviewAreaProps = {
  result: SearchResult;
  zoomLevel: number;
  currentPage: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomChange?: (level: number) => void;
  onNavigate?: (isNext: boolean) => void;
  onFileDownloadComplete?: (status: FileDownloadStatus) => void;
};

export const DocumentPreviewArea = memo(function DocumentPreviewArea({
  result,
  zoomLevel,
  currentPage,
  onZoomIn,
  onZoomOut,
  onZoomChange,
  onNavigate,
  onFileDownloadComplete,
}: DocumentPreviewAreaProps) {
  const { t } = useTranslation();
  // ref that will wrap FileViewer output to track page count
  const fileViewerWrapperRef = useRef<HTMLDivElement>(null);

  // Memoize the initial page count to avoid recalculation
  const initialPageCount = useMemo(
    () => getInitialPageCount(result),
    [result.uuid, result.mimeType, result.fileTypeInfo?.id],
  );

  const {
    currentPage: fvCurrentPage,
    totalPages: fvTotalPages,
    goToPage: goToFvPage,
  } = useFileViewerPagination(fileViewerWrapperRef);
  const [imageState, setImageState] = useState<"loading" | "loaded" | "error">(
    "loading",
  );
  const [fileViewerIsErrored, setFileViewerIsErrored] = useState(false);
  const [pdfCurrentPage, setPdfCurrentPage] = useState(currentPage);
  const [totalPages, setTotalPages] = useState(initialPageCount);

  const previewUrl = result.media?.previewUrl || result.previewUrl;

  // check to see if this is a Dropbox shared link that can be previewed with FileViewer
  const sharedLink = useMemo(() => {
    if (!result.url) return undefined;

    try {
      const url = new URL(result.url);

      const isDropboxDomain =
        url.hostname === "dropbox.com" ||
        url.hostname.startsWith("www.dropbox.com");
      const hasSharedLinkPath = url.pathname.includes("/scl/fi");

      return isDropboxDomain && hasSharedLinkPath ? result.url : undefined;
    } catch (error) {
      logger.warn("Invalid URL provided for shared link check:", error);
      return undefined;
    }
  }, [result.url, result.uuid]);

  const shouldUsePdfjs = useMemo(() => {
    if (isPdfFile(result)) {
      if (sharedLink && !fileViewerIsErrored) return false;

      logger.debug("Using PDF.js for PDF file", {
        url: result.url,
        title: result.title,
        hasPreviewUrl: !!previewUrl,
        hasSharedLink: !!sharedLink,
        fileViewerIsErrored,
      });

      return true;
    }

    return false;
  }, [
    result.uuid,
    result.url,
    result.title,
    sharedLink,
    fileViewerIsErrored,
    previewUrl,
  ]);

  // Use backend-provided file type categories for more robust image/video detection
  const isImageOrVideo =
    result.fileTypeInfo?.id === "image" || result.fileTypeInfo?.id === "video";

  // Google Drive files need special centering because they often render as embedded
  // documents that don't fill the preview area naturally, unlike PDFs or images
  const isGoogleDrive = result.connectorInfo?.connectorId === "gdrive";

  const handleImageLoad = () => {
    setImageState("loaded");
    onFileDownloadComplete?.("success");
  };

  const handleImageError = () => {
    setImageState("error");
    onFileDownloadComplete?.("error");
  };

  const handleFileViewerError = useCallback(
    (error: string) => {
      logger.warn("FileViewer error:", error);
      setFileViewerIsErrored(true);
      onFileDownloadComplete?.("error");
    },
    [onFileDownloadComplete],
  );

  const handlePdfPageChange = useCallback((page: number) => {
    setPdfCurrentPage(page);
  }, []);

  const handlePdfViewerUpdate = useCallback(
    (updates: { totalPages?: number }) => {
      if (updates.totalPages) {
        setTotalPages(updates.totalPages);
      }
    },
    [],
  );

  if (shouldUsePdfjs) {
    logger.debug("Rendering EnhancedFileViewer for PDF", {
      url: result.url,
      previewUrl: result.previewUrl,
      title: result.title,
    });

    return (
      <EnhancedFileViewer
        result={result}
        zoomLevel={zoomLevel}
        currentPage={pdfCurrentPage}
        onZoomIn={onZoomIn}
        onZoomOut={onZoomOut}
        onZoomChange={onZoomChange}
        onPageChange={handlePdfPageChange}
        onViewerUpdate={handlePdfViewerUpdate}
        onFileDownloadComplete={onFileDownloadComplete}
      />
    );
  }

  // checking to see if this is a Dropbox shared link and FileViewer hasn't errored -> use FileViewer
  if (sharedLink && !fileViewerIsErrored) {
    return (
      <div className={styles.previewArea}>
        <div className={styles.previewControls}>
          <div className={styles.paginationControls}>
            <Button
              variant="borderless"
              size="small"
              withIconStart={<UIIcon src={ChevronLeftLine} />}
              onClick={() => {
                goToFvPage(fvCurrentPage - 1);
                onNavigate?.(false);
              }}
              disabled={fvCurrentPage <= 1}
              aria-label={t("previous_page")}
            />
            <Button
              variant="borderless"
              size="small"
              withIconStart={<UIIcon src={ChevronRightLine} />}
              onClick={() => {
                goToFvPage(fvCurrentPage + 1);
                onNavigate?.(true);
              }}
              disabled={fvCurrentPage >= fvTotalPages}
              aria-label={t("next_page")}
            />
          </div>

          <div className={(styles as Record<string, string>).zoomGroup}>
            <Text size="small" className={styles.zoomInfo}>
              {t("page_of_total", {
                currentPage: fvCurrentPage,
                totalPages: fvTotalPages,
              })}
            </Text>

            <Menu.Wrapper
              onSelection={(value) => {
                const level = Number(value);
                if (!Number.isNaN(level) && onZoomChange) {
                  onZoomChange(level);
                }
              }}
            >
              {({ getTriggerProps, getContentProps }) => (
                <>
                  <Button
                    {...getTriggerProps()}
                    variant="outline"
                    size="medium"
                    withIconEnd={<UIIcon src={ChevronDownLine} />}
                    className={styles.zoomPercentage}
                  >
                    {t("zoom_percentage", { value: zoomLevel })}
                  </Button>
                  <Menu.Content {...getContentProps()} placement="top">
                    {ZOOM_LEVELS.map((level) => (
                      <Menu.ActionItem
                        key={level}
                        value={level.toString()}
                        withRightAccessory={
                          level === zoomLevel ? (
                            <UIIcon src={CheckmarkLine} size="small" />
                          ) : undefined
                        }
                      >
                        {t("zoom_percentage", { value: level })}
                      </Menu.ActionItem>
                    ))}
                  </Menu.Content>
                </>
              )}
            </Menu.Wrapper>
          </div>

          <div className={styles.zoomButtons}>
            <Button
              variant="borderless"
              size="medium"
              withIconStart={<UIIcon src={ZoomInLine} />}
              onClick={onZoomIn}
              disabled={zoomLevel >= 200}
            />
            <Button
              variant="borderless"
              size="medium"
              withIconStart={<UIIcon src={ZoomOutLine} />}
              onClick={onZoomOut}
              disabled={zoomLevel <= 50}
            />
          </div>
        </div>

        <div className={styles.documentPreview}>
          <div className={styles.documentPage}>
            <div
              className={styles.fileViewerZoomWrapper}
              ref={fileViewerWrapperRef}
              style={
                {
                  "--zoom-scale": zoomLevel / 100,
                } as React.CSSProperties
              }
            >
              <FileViewer
                sharedLinkUrl={sharedLink}
                fileName={result.title}
                reportError={handleFileViewerError}
              >
                <div />
              </FileViewer>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // if it's not a Dropbox shared link or FileViewer has errored, fall back to the existing image preview logic
  return (
    <div className={styles.previewArea}>
      {!isImageOrVideo && (
        <div className={styles.previewControls}>
          <div className={styles.zoomGroup}>
            <Text size="small" className={styles.zoomInfo}>
              {t("page_of_total", { currentPage, totalPages })}
            </Text>

            <Menu.Wrapper
              onSelection={(value) => {
                const level = Number(value);
                if (!Number.isNaN(level) && onZoomChange) {
                  onZoomChange(level);
                }
              }}
            >
              {({ getTriggerProps, getContentProps }) => (
                <>
                  <Button
                    {...getTriggerProps()}
                    variant="outline"
                    size="medium"
                    withIconEnd={<UIIcon src={ChevronDownLine} />}
                    className={styles.zoomPercentage}
                  >
                    {t("zoom_percentage", { value: zoomLevel })}
                  </Button>
                  <Menu.Content {...getContentProps()} placement="top">
                    {ZOOM_LEVELS.map((level) => (
                      <Menu.ActionItem
                        key={level}
                        value={level.toString()}
                        withRightAccessory={
                          level === zoomLevel ? (
                            <UIIcon src={CheckmarkLine} size="small" />
                          ) : undefined
                        }
                      >
                        {t("zoom_percentage", { value: level })}
                      </Menu.ActionItem>
                    ))}
                  </Menu.Content>
                </>
              )}
            </Menu.Wrapper>
          </div>

          <div className={styles.zoomButtons}>
            <Button
              variant="borderless"
              size="medium"
              withIconStart={<UIIcon src={ZoomInLine} />}
              onClick={onZoomIn}
              disabled={zoomLevel >= 200}
            />
            <Button
              variant="borderless"
              size="medium"
              withIconStart={<UIIcon src={ZoomOutLine} />}
              onClick={onZoomOut}
              disabled={zoomLevel <= 50}
            />
          </div>
        </div>
      )}

      <div
        className={styles.documentPreview}
        style={{
          // Center Google Drive files and images/videos for better visual presentation
          alignItems: isImageOrVideo || isGoogleDrive ? "center" : "flex-start",
        }}
      >
        <div className={styles.documentPage}>
          {previewUrl && imageState !== "error" ? (
            <div className={styles.thumbnailContainer}>
              {imageState === "loading" && (
                <div className={styles.thumbnailSkeleton}>
                  <Text size="small" color="subtle">
                    {t("loading")}
                  </Text>
                </div>
              )}

              {/* 
                Special-case handling below for Google Drive preview links.

                If the URL is from Google Drive in the format:
                  https://drive.google.com/file/d/<FILE_ID>/preview
                we extract the <FILE_ID> from the `result.url` and reconstruct the previewable iframe link.

                This is necessary because Google Drive links can come in various formats, 
                and we need to enforce the exact format required for embedding:
                  https://drive.google.com/file/d/<FILE_ID>/preview

                Example input: 
                  result.url = "https://drive.google.com/file/d/abc123/view?usp=sharing"

                This will match the regex `/\/d\/([A-Za-z0-9_-]+)/` and reconstruct:
                  iframe src = "https://drive.google.com/file/d/abc123/preview"
              */}
              {/* if fails, the user will see the previewUrl */}

              {isGoogleDrive ? (
                <iframe
                  src={(() => {
                    try {
                      const match = result.url?.match(/\/d\/([A-Za-z0-9_-]+)/);
                      if (match?.[1]) {
                        return `https://drive.google.com/file/d/${match[1]}/preview`;
                      }
                    } catch {
                      logger.warn("Invalid Google Drive URL", {
                        url: result.url,
                      });
                    }
                    return previewUrl;
                  })()}
                  title={result.title}
                  style={{ width: "100%", height: "600px", border: "none" }}
                  onLoad={handleImageLoad}
                />
              ) : (
                <img
                  src={previewUrl}
                  alt={t("document_preview_alt", { title: result.title })}
                  className={classNames(styles.thumbnailImage, {
                    [styles.thumbnailImageLoaded]: imageState === "loaded",
                    [styles.thumbnailImageLoading]: imageState === "loading",
                  })}
                  style={
                    {
                      "--zoom-scale": zoomLevel / 100,
                    } as React.CSSProperties
                  }
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                />
              )}
            </div>
          ) : (
            <div className={styles.placeholderContent}>
              <Text size="large" color="subtle">
                {t("no_preview_available")}
              </Text>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
